import { theme } from '@geneo2-web/shared-ui';
import { useMediaQuery } from '@mui/material';
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

// Define the shape of your context data
interface IContextData {
  selectedFunction: (() => void) | null;
  setSelectedFunction: (func: (() => void) | null) => void;
  showAppLoader: boolean;
  setShowAppLoader: (_val: boolean) => void;
  showPalModePopup: boolean;
  setShowPalModePopup: (_val: boolean) => void;
  palLoadingStatus: {
    uniqueId?: string;
    mode?: string;
  };
  setPalLoadingStatus: (_val: { uniqueId?: string; mode?: string }) => void;
  isIpadOnly: boolean;
  selectedLogoClickHandler: (() => void) | null;
  setSelectedLogoClickHandler: (func: (() => void) | null) => void;
  myPerformanceClickHandler?: (() => void) | null;
  setMyPerformanceClickHandler?: (func: (() => void) | null) => void;
}

// Create the context
const globalContext = createContext<IContextData | undefined>(undefined);

const { Provider } = globalContext;

// Create a provider component
interface IProviderProps {
  children: ReactNode;
}

export const GlobalContextProvider: React.FC<IProviderProps> = ({
  children,
}) => {
  const [selectedFunction, setSelectedFunction] = useState<(() => void) | null>(
    null
  );
  const [selectedLogoClickHandler, setSelectedLogoClickHandler] = useState<
    (() => void) | null
  >(null);
  const [myPerformanceClickHandler, setMyPerformanceClickHandler] = useState<
    (() => void) | null
  >(null);

  const [showAppLoader, setShowAppLoader] = useState<boolean>(false);
  const [showPalModePopup, setShowPalModePopup] = useState<boolean>(false);
  const [palLoadingStatus, setPalLoadingStatus] = useState<{
    uniqueId?: string;
    mode?: string;
  }>({
    uniqueId: undefined,
    mode: undefined,
  });

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
  const isIpadOnly = !isMobile && isIpad;
  useEffect(() => {
    const handlePopState = () => {
      if (selectedFunction) {
        selectedFunction();
        setSelectedFunction(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedFunction]);

  const contextValue: IContextData = {
    selectedFunction,
    setSelectedFunction,
    showAppLoader,
    setShowAppLoader,
    showPalModePopup,
    setShowPalModePopup,
    palLoadingStatus,
    setPalLoadingStatus,
    isIpadOnly,
    selectedLogoClickHandler,
    setSelectedLogoClickHandler,
    myPerformanceClickHandler,
    setMyPerformanceClickHandler,
  };
  return <Provider value={contextValue}>{children}</Provider>;
};

export const useGlobalContext = () => {
  const context = useContext(globalContext);
  if (!context) {
    throw new Error('useGlobalContext must be used within a MyContextProvider');
  }
  return context;
};
